import { create } from 'zustand';
import type { ThemeTemplate } from '@/types/sections';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const HISTORY_CAP = 50;

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

// Idle auto-commit: a continuous interaction (color drag, slider drag, text
// typing) coalesces into ONE history entry. The interaction ends on an
// explicit commitInteraction() (e.g. ColorField/TextField onBlur) OR after
// this idle gap with no further transient edit — so controls without a
// reliable blur signal (RangeSlider mid-drag) still yield one undo step
// per gesture.
const IDLE_COMMIT_MS = 350;
let idleCommitTimer: ReturnType<typeof setTimeout> | null = null;
function clearIdleCommit() {
  if (idleCommitTimer) {
    clearTimeout(idleCommitTimer);
    idleCommitTimer = null;
  }
}

interface EditorState {
  /** In-memory working copy. All edits mutate this; Save flushes it. */
  staged: ThemeTemplate | null;
  /** Which page-template `staged` belongs to. */
  stagedTemplate: string | null;
  /** Undo/redo ring over `staged` (capped at HISTORY_CAP). */
  past: ThemeTemplate[];
  future: ThemeTemplate[];
  dirty: boolean;
  saveStatus: SaveStatus;
  lastSavedAt: number | null;
  /** True while a continuous interaction (color drag, text typing) is in
   *  progress — prevents per-tick history pushes. Reset by commitInteraction()
   *  or any discrete edit (applyEdit). Non-persisted. */
  interactionActive: boolean;

  /**
   * Adopt the server draft as the staged base. Once a template is adopted
   * for the session it is NOT re-cloned (the draft query is manual-control:
   * staleTime Infinity + no focus refetch, and post-save setQueryData
   * injects the saved blob) — this preserves the staged copy AND the
   * undo history across saves. Only a template switch / first load / a
   * post-discard null staged triggers a fresh adopt + history reset.
   */
  hydrate: (template: string, json: ThemeTemplate) => void;
  /** Apply a discrete in-memory mutation — pushes to history once. */
  applyEdit: (mutator: (t: ThemeTemplate) => void) => void;
  /** Apply a continuous mutation (color drag, text typing) — coalesces
   *  history entries. Call commitInteraction() when the interaction ends. */
  applyTransientEdit: (mutator: (t: ThemeTemplate) => void) => void;
  /** Mark the current continuous interaction as finished. No history push. */
  commitInteraction: () => void;
  undo: () => void;
  redo: () => void;
  /** Drop staged edits + history; the next hydrate re-adopts the draft. */
  discard: () => void;
  setSaveStatus: (status: SaveStatus) => void;
  /** Mark the current staged state as the saved baseline (history kept). */
  markSaved: () => void;
  /** Discrete edit: merge a partial object into staged.globalSettings[section]. */
  setGlobalSetting: (section: string, partial: Record<string, unknown>) => void;
  /** Transient edit (continuous): same merge, but coalesces history. Pair
   *  with commitInteraction() on blur/close. */
  setGlobalSettingTransient: (section: string, partial: Record<string, unknown>) => void;
}

export const useEditorStore = create<EditorState>((set, get) => ({
  staged: null,
  stagedTemplate: null,
  past: [],
  future: [],
  dirty: false,
  saveStatus: 'idle',
  lastSavedAt: null,
  interactionActive: false,

  hydrate: (template, json) => {
    const st = get();
    if (st.stagedTemplate === template && st.staged) return;
    clearIdleCommit();
    set({
      staged: clone(json),
      stagedTemplate: template,
      past: [],
      future: [],
      dirty: false,
      interactionActive: false,
    });
  },

  applyEdit: (mutator) => {
    const st = get();
    if (!st.staged) return;
    clearIdleCommit();
    const next = clone(st.staged);
    mutator(next);
    set({
      staged: next,
      past: [...st.past, st.staged].slice(-HISTORY_CAP),
      future: [],
      dirty: true,
      saveStatus: 'idle',
      interactionActive: false,
    });
  },

  applyTransientEdit: (mutator) => {
    const st = get();
    if (!st.staged) return;
    const next = clone(st.staged);
    mutator(next);
    if (st.interactionActive) {
      // Coalesce: do NOT push to past
      set({ staged: next, dirty: true, saveStatus: 'idle' });
    } else {
      // First tick of a continuous interaction: push pre-edit snapshot
      set({
        staged: next,
        past: [...st.past, st.staged].slice(-HISTORY_CAP),
        future: [],
        dirty: true,
        saveStatus: 'idle',
        interactionActive: true,
      });
    }
    // (Re)arm the idle window. If the gesture has no blur signal the timer
    // ends the interaction so the next edit starts a fresh undo entry.
    clearIdleCommit();
    idleCommitTimer = setTimeout(() => {
      idleCommitTimer = null;
      if (get().interactionActive) set({ interactionActive: false });
    }, IDLE_COMMIT_MS);
  },

  commitInteraction: () => {
    clearIdleCommit();
    set({ interactionActive: false });
  },

  undo: () => {
    const st = get();
    if (!st.staged || st.past.length === 0) return;
    clearIdleCommit();
    const prev = st.past[st.past.length - 1];
    set({
      staged: prev,
      past: st.past.slice(0, -1),
      future: [st.staged, ...st.future],
      dirty: true,
      saveStatus: 'idle',
      interactionActive: false,
    });
  },

  redo: () => {
    const st = get();
    if (!st.staged || st.future.length === 0) return;
    clearIdleCommit();
    const next = st.future[0];
    set({
      staged: next,
      future: st.future.slice(1),
      past: [...st.past, st.staged],
      dirty: true,
      saveStatus: 'idle',
      interactionActive: false,
    });
  },

  discard: () => {
    clearIdleCommit();
    set({
      staged: null,
      stagedTemplate: null,
      past: [],
      future: [],
      dirty: false,
      saveStatus: 'idle',
      interactionActive: false,
    });
  },

  setSaveStatus: (saveStatus) => set({ saveStatus }),

  markSaved: () =>
    set({ dirty: false, saveStatus: 'saved', lastSavedAt: Date.now() }),

  setGlobalSetting: (section, partial) => {
    get().applyEdit((t) => {
      t.globalSettings = {
        ...t.globalSettings,
        [section]: { ...(t.globalSettings?.[section] ?? {} as Record<string, unknown>), ...partial },
      };
    });
  },

  setGlobalSettingTransient: (section, partial) => {
    get().applyTransientEdit((t) => {
      t.globalSettings = {
        ...t.globalSettings,
        [section]: { ...(t.globalSettings?.[section] ?? {} as Record<string, unknown>), ...partial },
      };
    });
  },
}));
